/**
 * Lưới ô cố định cho lazy-load bản đồ vùng trồng chung.
 *
 * Vì sao là lưới chứ không phải khung nhìn: lấy thẳng khung nhìn làm khoá cache
 * thì pan lệch 1 pixel là một khoá mới → tải lại liên tục, cache vô dụng. Chia
 * bản đồ thành ô 0,05° (≈5,5 km) thì khung nhìn phủ ô nào chỉ tải ô đó, ô đã có
 * trong cache không gọi lại — pan qua lại trong vùng đã xem không tốn request.
 */

export const TILE_DEG = 0.05

/** Dưới mức zoom này (nhìn cả huyện) không tải polygon — chỉ nhắc phóng to. */
export const MIN_LOAD_ZOOM = 12

/** Dưới mức zoom này nhãn "hộ / vùng" chồng lên nhau nên ẩn đi. */
export const MIN_LABEL_ZOOM = 14

/**
 * Chặn trên số ô một khung nhìn được phép kéo. Zoom 12 (≈36 m/px ở 21°N) trên
 * màn 1920×1080 đã là ~13×6 = 78 ô, màn 4K ~27×13 = 350 ô; ngưỡng 64 cũ làm
 * màn 1920 ở đúng zoom tối thiểu trống trơn. Vượt ngưỡng → overlay coi như
 * "quá xa" và hiện nhắc phóng to, không tải.
 */
export const MAX_TILES_PER_VIEW = 200

export type TileKey = { latIdx: number; lngIdx: number }

export type Bounds = {
    north: number
    south: number
    east: number
    west: number
}

/** Chỉ số ô chứa một toạ độ. Cộng epsilon để 21.55 / 0.05 = 430.9999… không lùi ô. */
function tileIndex(deg: number): number {
    return Math.floor(deg / TILE_DEG + 1e-9)
}

export function tileId(t: TileKey): string {
    return `${t.latIdx}:${t.lngIdx}`
}

/** Khung bao của một ô (cạnh trên/phải thuộc ô kế tiếp, nhưng bbox giao nên không hụt). */
export function tileBounds(t: TileKey): Bounds {
    return {
        south: t.latIdx * TILE_DEG,
        north: (t.latIdx + 1) * TILE_DEG,
        west: t.lngIdx * TILE_DEG,
        east: (t.lngIdx + 1) * TILE_DEG,
    }
}

/**
 * Các ô mà khung nhìn phủ lên, theo thứ tự lat rồi lng. Khung nhìn vắt qua
 * kinh tuyến 180° (east < west) không xảy ra ở Việt Nam → coi là rỗng thay vì
 * tải vòng quanh trái đất.
 */
export function tilesForBounds(b: Bounds): TileKey[] {
    if (b.east < b.west || b.north < b.south) return []
    const latFrom = tileIndex(b.south)
    const latTo = tileIndex(b.north)
    const lngFrom = tileIndex(b.west)
    const lngTo = tileIndex(b.east)
    const count = (latTo - latFrom + 1) * (lngTo - lngFrom + 1)
    if (count > MAX_TILES_PER_VIEW) return []
    const out: TileKey[] = []
    for (let latIdx = latFrom; latIdx <= latTo; latIdx++) {
        for (let lngIdx = lngFrom; lngIdx <= lngTo; lngIdx++) {
            out.push({ latIdx, lngIdx })
        }
    }
    return out
}
